<template>
  <div>
    <div class="overflow-x-hidden relative rounded-md">
      <!-- Border -->
      <div
        class="absolute top-0 left-0 w-full rounded-md border border-border-normal z-10 pointer-events-none select-none"
        :style="borderContainerStyle" />
      <!-- Query loader -->
      <div v-if="loading"
        class="bg-gray-100 opacity-50 rounded-md absolute top-0 left-0 w-full h-full z-50 flex justify-center items-center">
        <BaseLoadingSpinnerCircle class="text-icon-normal w-8 h-8" />
      </div>
      <div ref="scrollContainer" class="w-full overflow-x-auto scroll-container">
        <table class="relative">
          <thead>
            <tr>
              <!-- Grouped by column -->
              <th class="sticky-column">
                <div class="group-cell border-r flex items-center px-2 py-3 h-10 gap-3 ">
                  <input v-if="showSelectAll" type="checkbox" class="checkbox-input" :disabled="disableToggleAll"
                    :checked="!disableToggleAll && preventRowAllAdd" @change="toggleAllRows(false)">
                  <BaseText type="heading" size="overline" class="text-text-subdued uppercase">
                    {{ isAdLevel ? 'Ad Name' : groupedBy?.replace(/_/g, ' ') }}
                  </BaseText>
                  <div class="h-6 w-20 select-row-wrapper overflow-hidden" v-if="selectedRows.length > 0">
                    <div class="select-row-info  w-full cursor-pointer underline text-text-disabled whitespace-nowrap"
                      @click="toggleAllRows(true)">
                      <BaseText type="heading" size="overline">({{ selectedRows.length }} selected)</BaseText>
                      <BaseText type="heading" size="overline">Unselect all</BaseText>
                    </div>
                  </div>
                  <!-- Toggle floating column header -->
                  <BaseTooltipV2
                    v-if="enableFloatHeaderToggle"
                    :text="floatHeaderEnabled ? 'Unpin column headers' : 'Pin column headers to top of page'"
                    position="right" :delay="500" class="ml-auto flex-shrink-0" style="z-index: 50000">
                    <button class="w-6 h-6 flex items-center justify-center rounded-md text-icon-normal
                    transition-colors duration-100 hover:bg-background-normal hover:text-icon-muted"
                      @click="toggleFloatHeader">
                      <PinOutlineIcon v-if="floatHeaderEnabled" :width="16" :height="16" />
                      <UnpinIcon v-else :width="16" :height="16" />
                    </button>
                  </BaseTooltipV2>
                </div>
              </th>
              <!-- Other selected kpis -->
              <th v-for="(header, hIndex) in tableKpis" :key="`${header.key}-${hIndex}`" :ref="`headerKey`"
                :class="{ 'sticky-column': header.is_pinned, 'header-column': true }"
                :style="header.is_pinned ? { left: `${calculatePinOffset(header)}px` } : {}">
                <!-- TODO: @Nate - Allow FP metrics to be selected for line graph when support is added -->
                <HeaderActionCell 
                  :disableActions="['status', 'tags', 'creation_date_range'].includes(header.key)" 
                  :header="header"
                  :disablePin="isCategorizedTable" 
                  :disableSelect="isCategorizedTable"
                  :isSelected="selectedColumns.includes(header.key)"
                  :columnColor="graphInfo.selectedGraph === 'bar' ? getColumnColor(header) : ''"
                  :canSelectCol="!preventColAdd && !(graphInfo.selectedGraph === 'line' && isForeplayMetric(header.key))" 
                  :currentSort="currentSort" 
                  :isAdLevel="isAdLevel"
                  :isMetadata="header.is_metadata" 
                  @pin="pinColumn" 
                  @sort="sortColumn" 
                  @select="selectColumn" 
                />
              </th>
            </tr>
          </thead>
          <!-- START OF COLLAPSIBLE ROWS -->
          <template v-if="isCategorizedTable">
            <tbody v-for="(group, groupIndex) in categorizedRows.ad_groups" :key="groupIndex">
              <!-- Sticky Group Header Row -->
              <tr class="relative">
                <td class="sticky-column disable-bg-hover">
                  <div
                  class="w-full px-2 py-1.5 flex items-center gap-2 border-b border-border-normal bg-background-soft h-10 ">
                    <button @click="handleCategoryCollapse(group.group_by)" class="p-1 rounded-md hover:bg-background-normal">
                      <ChevronIcon class=" transition-transform transform text-icon-normal"
                      :class="{ '-rotate-90': !hiddenCategorizedRows?.[group.group_by] }" />
                    </button>
                    <BaseText type="label" size="sm" class="text-text-muted">{{ formatLaunchDate(group.group_by) }}</BaseText>
                    <BaseText size="xs" class="text-text-subdued">{{ getCategorizedRowCountLabel(group.ads.length) }}</BaseText>
                  </div>
                </td>
                <!-- Used to mimick bottom border of launch date row ( MUST BE AFTER DATE TD ) -->
                <div class="border-border-normal border-b absolute bottom-0 left-0 w-full h-full"/>
              </tr>

              <!-- Grouped Rows -->
              <template v-if="hiddenCategorizedRows?.[group.group_by]">
                <tr  v-for="(row, index) in group.ads" :key="`${row.ad_name}-${row.ad_id}-${index}`">
                  <td class="sticky-column table-cell">
                    <div class="group-cell border-r grid grid-cols-12 py-1 px-2 gap-x-3 items-center"
                      style="height: 45px;">
                      <!-- Middle info -->
                      <div class="ml-1 col-span-9">
                        <div class="flex items-center flex-nowrap gap-2 min-w-0" :class="{ 'py-1': isAdLevel }">
                          <!-- Group image -->
                          <div class="flex-shrink-0" v-if="row?.ad_image_url || row.ads.length > 0">
                            <img :src="row.ad_image_url" class="rounded-md w-7 h-7  bg-cover">
                          </div>
                          <!-- Group title & ad count -->
                          <div class="flex-1 min-w-0 whitespace-nowrap relative">
                            <div
                            @mouseenter="showTooltip('groupTooltip', true, $event, row.ad_names)"
                            @mouseleave="showTooltip('groupTooltip', false, $event)"
                            >
                              <BaseText v-clipboard:copy="row.ad_name || row.ad_name"
                                v-clipboard:success="handleCopySuccess"
                                class="text-text-muted truncate cursor-pointer max-w-min" size="sm">
                                {{ row.ad_name }}
                              </BaseText>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-span-2">
                        <div class="flex items-center min-w-0">
                          <button v-show="isAdDetailsEnabled" @click="() => $emit('showDetailsDrawer', row)"
                            class="p-1" @mouseenter="hoveredInsightsBtnId = row.creative_hash"
                            @mouseleave="hoveredInsightsBtnId = null">
                            <AdDetailsAnimatedIcon class="cursor-pointer text-icon-normal"
                              :isHovered="hoveredInsightsBtnId === row.creative_hash" />
                          </button>
                          <button
                            class="p-1"
                            :data-tour="index === 0 ? 'lens-row-breakdown' : null"
                            @mouseenter="hoveredDetailsBtnId = row.creative_hash"
                            @mouseleave="hoveredDetailsBtnId = null"
                            @click="() => $emit('showGroupBreakdown', row)"
                          >
                            <GroupDetailsIcon class="cursor-pointer text-icon-normal"
                              :isHovered="hoveredDetailsBtnId === row.creative_hash" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <!-- Virutalization padding -->
                  <td v-for="(kpi, i) in tableKpis.slice(0, startIndex)" :key="`pad-${i}`" class="table-cell"/>
                  <!-- Selected KPI Columns -->
                  <td v-for="(kpi, colIndex) in visibleKpis" :key="`${kpi.key}-${row.ad_id}-${row.group_by}`"
                    :ref="`td-${kpi.key}`" class="table-cell"
                    :style="getCellStyle(kpi, row, `${kpi.key}-${row.ad_id}-${row.group_by}-${tableConfig.colorFormat}${isCategorizedTable ? `-${loading}` : ''}`)">
                    <div class="cell relative" style="height: 45px">
                      <StatusCell class="px-3 py-2.5" v-if="kpi.key === 'status' && tableConfig?.showStatus" :data="{
                        value: (isAdLevel || isCategorizedTable)
                          ? { state: row.ad_status }
                          : { active: row.active_ad_count, paused: row.paused_ad_count }
                      }" :isAdLevel="isAdLevel || isCategorizedTable" />
                      <TagCell :tags="row?.custom_tags" class="px-3 py-1.5" v-else-if="kpi?.key === 'tags'" :isAdLevel="isAdLevel" @selectTag="bindTagToAd(row, $event, index)" @removeTag="removeTagFromAd(row, $event, index)"/>
                      <CreationDateRangeCell 
                        v-else-if="kpi?.key === 'creation_date_range'" 
                        :dateRange="{ start: row?.overall_min_ad_created_at, end: row?.overall_max_ad_created_at }" 
                      />
                      <TableCell v-else class="p-3" :color-format="getColoredCell(row, kpi).direction"
                        :metric-direction="getMetricLookup?.[kpi.key]?.metric_direction"
                        :avg-diff="row?.percent_deviation?.[kpi.key]"
                        :cell-type="getMetricLookup?.[kpi.key]?.type || 'text'" :cell-value="row?.[kpi.key]"
                        :is-metadata="kpi?.is_metadata" 
                        :is-url="kpi.key === 'destination_url'"
                        :is-date="kpi.key === 'ad_created_at'"
                        :currency="lensCurrency" />
                    </div>
                  </td>
                </tr>
                <!-- Per-Category Net Results -->
                <tr>
                  <td class="sticky-column">
                    <div class="cell py-3 pl-4 bg-background-disabled h-full flex gap-3 items-center">
                      <div class="relative group" @click="navToNetInfo"
                      @mouseenter="showTooltip('checkboxInclude', true, $event, 'Click here to learn more')"
                      @mouseleave="showTooltip('checkboxInclude', false, $event)">
                        <InformationIcon class="text-icon-disabled group-hover:text-icon-normal cursor-pointer" />
                      </div>
                      <div>
                        <BaseText size="sm" type="body" class="text-text-normal">
                          Day Net Results <span class="text-text-subdued font-normal">({{ formatLaunchDate(group.group_by) }})</span>
                        </BaseText>
                      </div>
                    </div>
                  </td>
                  <td
                    v-for="(kpi,kpiIndex) in tableKpis"
                    :key="`${group.group_by}-net-${kpi}-${kpiIndex}`"
                    :class="{ 'sticky-column': kpi.is_pinned }"
                    :style="kpi.is_pinned ? { left: `${calculatePinOffset(kpi)}px` } : ''"
                  >
                    <div v-if="!kpi.is_metadata" class="cell flex items-center gap-3 bg-background-disabled" style="height:45px" :class="kpi.key === 'status' ? 'p-2.5' : 'p-3'">
                      <div v-if="kpi.key !== 'status' && getKpiType(kpi) !== 'percentile'" class="flex items-center gap-3 flex-nowrap">
                        <TableCell
                          hide-tooltip
                          class="flex-shrink-0"
                          :cell-type="getMetricLookup?.[kpi.key]?.type"
                          :cell-value="group?.[kpi.key]"
                          :currency="lensCurrency"
                        />
                        <!-- <BaseText size="sm" class="whitespace-nowrap  truncate text-text-muted">
                            Avg. {{ getIsPrivateMode ? '•••' :  formatNumber(group.weighted_averages?.[kpi.key], getMetricLookup?.[kpi.key]?.type, lensCurrency) }} 
                        </BaseText> -->
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </template>
          <!-- END OF COLLAPSIBLE ROWS -->
          <tbody v-else>
            <tr v-for="(row, index) in rows" :key="`${row}-${index}`">
              <td class="sticky-column table-cell min-w-0">
                <div class="group-cell border-r flex py-1 px-2 gap-x-3 items-center min-w-0">
                  <!-- Left checkbox -->
                  <div class="flex-shrink-0" :data-tour="index < 5 ? 'lens-row-checkbox' : null">
                    <input :ref="`checkbox-${row.group_by}`" :checked="isRowSelected(row.group_by || row.ad_id)"
                      class="checkbox-input"
                      :style="{ backgroundColor: isRowSelected(row.group_by || row.ad_id) ? getRowCheckboxColor(row.group_by) : '' }"
                      type="checkbox"
                      :disabled="(!isRowSelected(row.group_by || row.ad_id) && preventRowAdd) || (isAdLevel ? !row.ad_name : !row.group_by)"
                      @change="selectRow(row)"
                      @mouseenter="showTooltip('checkboxInclude', true, $event, isRowSelected(row.group_by || row.ad_id) ? 'Remove from visualization' : 'Include in visualization')"
                      @mouseleave="showTooltip('checkboxInclude', false, $event)">
                  </div>
                  <!-- Middle info -->
                  <div class="pl-1 flex-grow min-w-0">
                    <div class="w-full flex items-center flex-nowrap gap-2 min-w-0" :class="{ 'py-1': isAdLevel }">
                      <!-- Group image -->
                      <div class="flex-shrink-0" v-if="row?.ad_image_url || row.ads.length > 0">
                        <img :src="isAdLevel ? row.ad_image_url : row.ads?.[0].ad_image_url"
                          class="rounded-md w-7 h-7  bg-cover">
                      </div>
                      <!-- Group title & ad count -->
                      <div class="flex-grow flex flex-col min-w-0 whitespace-nowrap relative">
                        <div @mouseenter="showTooltip('groupTooltip', true, $event, getGroupLabels(row))"
                        @mouseleave="showTooltip('groupTooltip', false, $event)" class="min-w-0 truncate">
                          <BaseText v-clipboard:copy="getRowName(row)"
                          v-clipboard:success="handleCopySuccess"
                          class="block w-full text-text-muted truncate cursor-pointer" size="sm">
                            {{ getRowName(row) }}
                          </BaseText>
                        </div>
                        <div class="flex items-center" style="gap: 3px;">
                          <BaseTooltipV2 v-if="isCreativeGrouping" text="Same Creative" :offset="2">
                            <SameCreativeIcon class="text-icon-normal transition-colors duration-100 hover:text-text-muted" :width="16" :height="16" />
                          </BaseTooltipV2>
                          <BaseText v-if="!isAdLevel" class="text-text-normal" size="xs">
                            {{ getRowAdCount(row)?.toLocaleString() }} {{ getRowAdCount(row) > 1 ? 'Ads' : 'Ad' }}
                          </BaseText>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- CTA actions -->
                  <div class="flex-shrink-0">
                    <div class="flex items-center min-w-0">
                      <button v-show="isAdDetailsEnabled" @click="() => $emit('showDetailsDrawer', row)" class="p-1"
                      @mouseenter="hoveredInsightsBtnId = row.group_by || row.ad_id"
                      @mouseleave="hoveredInsightsBtnId = null">
                        <AdDetailsAnimatedIcon class="cursor-pointer text-icon-normal"
                          :isHovered="hoveredInsightsBtnId === (row.group_by || row.ad_id)" />
                      </button>
                      <button
                        v-if="!isAdLevel && (reportType === 'comparative' || row?.ads?.[0]?.[groupedBy] || groupedBy === 'creative')"
                        :data-tour="index === 0 ? 'lens-row-breakdown' : null"
                        class="p-1"
                        @mouseenter="hoveredDetailsBtnId = row.group_by"
                        @mouseleave="hoveredDetailsBtnId = null"
                        @click="() => $emit('showGroupBreakdown', row)"
                      >
                        <GroupDetailsIcon class="cursor-pointer text-icon-normal"
                        :isHovered="hoveredDetailsBtnId === row.group_by" />
                      </button>
                    </div>
                  </div>
                </div>
              </td>
              <!-- Selected KPI Columns -->
              <td v-for="(kpi, colIndex) in tableKpis" :key="`${kpi}-${colIndex}`" :ref="`td-${kpi.key}`"
                class="table-cell " :class="{ 'sticky-column': kpi.is_pinned }" :style="[
                  kpi.is_pinned ? { left: `${calculatePinOffset(kpi)}px` } : null,
                  row?.[kpi.key] !== 0 ? { backgroundColor: getColoredCell(row, kpi).scoreColor } : null,
                  { maxWidth: kpi?.is_metadata ? '222px' : null }
                ]">
                <div class="cell relative">
                  <!-- Handle status & tag cells -->
                  <StatusCell v-if="kpi.key === 'status' && tableConfig?.showStatus" 
                    class="px-3 py-2.5"  
                    :data="{
                      value: isAdLevel
                        ? { state: row.ad_status }
                        : { active: row.active_ad_count, paused: row.paused_ad_count }
                    }" 
                    :isAdLevel="isAdLevel" 
                  />
                  <TagCell v-else-if="kpi.key === 'tags'" :key="row?.group_by || row?.ad_id" 
                    class="px-3 py-1.5"
                    :tags="row?.custom_tags"
                    :isAdLevel="isAdLevel" 
                    @selectTag="bindTagToAd(row, $event, index)" 
                    @removeTag="removeTagFromAd(row, $event, index)"
                  />
                  <CreationDateRangeCell 
                    v-else-if="kpi?.key === 'creation_date_range'" 
                    :dateRange="{ start: row?.overall_min_ad_created_at, end: row?.overall_max_ad_created_at }" 
                  />
                  <TableCell v-else class="p-3" :color-format="getColoredCell(row, kpi).direction"
                    :metric-direction="getMetricLookup?.[kpi.key]?.metric_direction"
                    :avg-diff="row?.percent_deviation?.[kpi.key]"
                    :cell-type="getMetricLookup?.[kpi.key]?.type || 'text'" 
                    :cell-value="row?.[kpi.key]"
                    :is-metadata="kpi?.is_metadata" 
                    :is-url="kpi.key === 'destination_url'"
                    :is-date="kpi.key === 'ad_created_at'" 
                    :currency="lensCurrency" 
                  />
                </div>
              </td>
            </tr>
          </tbody>
          <!-- Table footer (Net results across all groupings) -->
          <tfoot v-if="!isCategorizedTable">
            <tr>
              <td class="sticky-column">
                <div class="group-cell py-3 px-1.5 bg-background-disabled h-full flex gap-3 items-center">
                  <div class="relative group" @click="navToNetInfo"
                    @mouseenter="showTooltip('checkboxInclude', true, $event, 'Click here to learn more')"
                    @mouseleave="showTooltip('checkboxInclude', false, $event)">
                    <InformationIcon class="text-icon-disabled group-hover:text-icon-normal cursor-pointer" />
                  </div>
                  <div class="flex-1">
                   <BaseText size="sm" type="label" class="text-text-muted col-span-10">
                      Net Results
                    </BaseText>
                  </div>
                  <div class="flex items-center gap-1">
                    <button class="group hover:bg-background-hover p-0.5 rounded-md transition-colors" @click="toggleNetResults('showSummation')">
                      <SummationIcon class="text-icon-disabled group-hover:text-icon-normal w-5 h-5 flex-shrink-0"
                      :class="{ 'text-icon-normal': tableConfig.showSummation }"
                      />
                    </button>
                    <button class="group hover:bg-background-hover p-0.5 rounded-md transition-colors" @click="toggleNetResults('showWeightedAvg')">
                      <WeightIcon class="text-icon-disabled group-hover:text-icon-normal w-5 h-5 flex-shrink-0"
                      :class="{ 'text-icon-normal': tableConfig.showWeightedAvg }"
                      />
                    </button>
                  </div>
                </div>
              </td>
              <td
                v-for="(kpi,kpiIndex) in tableKpis"
                :key="`net-${kpi}-${kpiIndex}`"
                :class="{ 'sticky-column': kpi.is_pinned }"
                :style="kpi.is_pinned ? { left: `${calculatePinOffset(kpi)}px` } : ''"
              >
                <div v-if="!kpi.is_metadata" class="cell flex items-center gap-3 bg-background-disabled" style="height:45px" :class="kpi.key === 'status' ? 'p-2.5' : 'p-3'">
                  <div v-if="kpi.key !== 'status'">
                    <FooterCell :kpi="kpi?.key" :kpi-type="getKpiType(kpi)" :net-kpi="formatNumber(netResults?.[kpi?.key], getMetricLookup?.[kpi.key]?.type, lensCurrency)" :weighted-avg-value="formatNumber(netResults.weighted_averages?.[kpi.key], getMetricLookup?.[kpi.key]?.type, lensCurrency)" :mean-avg-value="formatNumber(netResults.summary_stats?.ad_groups?.avg?.[kpi.key], getMetricLookup?.[kpi.key]?.type, lensCurrency)" :isAdLevel="isAdLevel" :show-weighted-avg="tableConfig.showWeightedAvg" :show-summation="tableConfig.showSummation"/>
                  </div>
                  <div v-else-if="kpi.key === 'status' && !isCategorizedTable" class="flex items-center gap-1 overflow-hidden">
                    <StatusCell :data="{value: {state: 'ACTIVE'}}" is-ad-level :overrideStatus="`${netResults.active_ad_count}`"/>
                    <StatusCell :data="{value: {state: 'PAUSED'}}" is-ad-level :overrideStatus="`${netResults.paused_ad_count}`"/>
                  </div>
                </div>
                <!-- If it's an ad-level metadata column, we display the number of unique values -->
                <div v-else class="cell p-3 bg-background-disabled">
                  <BaseText size="sm" class="text-text-muted" style="max-width: 132px">
                    {{ getMetadataNetResultLabel(kpi) }}
                  </BaseText>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <!-- Floating Header Clone (kept mounted while the feature is enabled so
           the static <-> floating toggle is a cheap display flip instead of a
           full mount/unmount hitch) -->
      <div 
        v-if="floatHeaderEnabled"
        v-show="showFloatingHeader"
        ref="floatingHeader" 
        class="fixed bg-white rounded-md border border-border-normal"
        style="z-index: 20; pointer-events: auto; overflow-x: hidden; overflow-y: hidden;"
        :style="floatingHeaderPosition">
        <table :style="floatingTableStyle">
          <colgroup>
            <col v-for="(width, index) in columnWidths" :key="`col-${index}`" :style="{ width: width + 'px' }" />
          </colgroup>
          <thead>
            <tr>
              <!-- Grouped by column -->
              <th class="sticky-column" :style="floatingHeaderCellStyle(0)">
                <div class="group-cell border-r flex items-center px-2 py-3 h-10 gap-3 ">
                  <input v-if="showSelectAll" type="checkbox" class="checkbox-input" :disabled="disableToggleAll"
                    :checked="!disableToggleAll && preventRowAllAdd" @change="toggleAllRows(false)">
                  <BaseText type="heading" size="overline" class="text-text-subdued uppercase">
                    {{ isAdLevel ? 'Ad Name' : groupedBy?.replace(/_/g, ' ') }}
                  </BaseText>
                  <div class="h-6 w-20 select-row-wrapper overflow-hidden" v-if="selectedRows.length > 0">
                    <div class="select-row-info  w-full cursor-pointer underline text-text-disabled whitespace-nowrap"
                      @click="toggleAllRows(true)">
                      <BaseText type="heading" size="overline">({{ selectedRows.length }} selected)</BaseText>
                      <BaseText type="heading" size="overline">Unselect all</BaseText>
                    </div>
                  </div>
                  <!-- Toggle floating column header -->
                  <BaseTooltipV2
                  v-if="enableFloatHeaderToggle"
                    :text="floatHeaderEnabled ? 'Unpin column headers' : 'Pin column headers to top of page'"
                    position="right" :delay="500" class="ml-auto flex-shrink-0" style="z-index: 50000">
                    <button class="w-6 h-6 flex items-center justify-center rounded-md text-icon-normal
                    transition-colors duration-100 hover:bg-background-normal hover:text-icon-muted"
                      @click="toggleFloatHeader">
                      <PinOutlineIcon v-if="floatHeaderEnabled" :width="16" :height="16" />
                      <UnpinIcon v-else :width="16" :height="16" />
                    </button>
                  </BaseTooltipV2>
                </div>
              </th>
              <!-- Other selected kpis -->
              <th v-for="(header, hIndex) in tableKpis" :key="`floating-${header.key}-${hIndex}`"
                :class="{ 'sticky-column': header.is_pinned, 'header-column': true }"
                :style="floatingHeaderCellStyle(hIndex + 1, header)">
                <HeaderActionCell 
                  :disableActions="['status', 'tags', 'creation_date_range'].includes(header.key)" 
                  :header="header"
                  :disablePin="isCategorizedTable" 
                  :disableSelect="isCategorizedTable"
                  :isSelected="selectedColumns.includes(header.key)"
                  :columnColor="graphInfo.selectedGraph === 'bar' ? getColumnColor(header) : ''"
                  :canSelectCol="!preventColAdd && !(graphInfo.selectedGraph === 'line' && isForeplayMetric(header.key))" 
                  :currentSort="currentSort" 
                  :isAdLevel="isAdLevel"
                  :isMetadata="header.is_metadata" 
                  @pin="pinColumn" 
                  @sort="sortColumn" 
                  @select="selectColumn" 
                />
              </th>
            </tr>
          </thead>
        </table>
      </div>
      <TablePagination class="mr-12 ml-2 mb-1" v-if="tablePagination?.totalPages !== 0 && !hidePagination"
        :showing="rows.length" :tablePagination="tablePagination"
        @updatePagination="$event => $emit('paginationChanged', { type: null, value: $event })" />
      <portal-target name="sort-dropdown">
        <!-- Sort dropdown -->
      </portal-target>
      <portal-target name="lens-tag-popover">
        <!-- Tag popover -->
      </portal-target>
    </div>
    <!-- Modals & Global tooltips -->
    <ColumnTooltips ref="tooltipContainer" :graphInfo="graphInfo" />
  </div>
</template>

<script>
import LensAPI from "@/api/lens"
import { mapActions, mapGetters } from 'vuex'
import kpiColors from '../../../utils/lens/selectedMetricColors'
import { getRelativeMetricScoreColor } from "../../../utils/lens/metricScoreUtil"
import {formatNumberLabel} from '../../../utils/lens/formatNumberLabel'
import ColumnTooltips from './ColumnTooltips.vue'
// Table cell components
import TablePagination from './TablePagination.vue'
import TableCell from './cells/TableCell.vue'
import StatusCell from './cells/StatusCell.vue'
import TagCell from './cells/TagCell.vue'
import CreationDateRangeCell from './cells/CreationDateRangeCell.vue'
import FooterCell from './cells/FooterCell.vue'
import HeaderActionCell from './cells/HeaderActionCell.vue'

// Icons
import AdDetailsAnimatedIcon from '../../globals/Icons/AdDetailsAnimatedIcon.vue'
import GroupDetailsIcon from '../../globals/Icons/LensIcons/GroupDetailsAnimatedIcon.vue'
import SortColumnIcon from '../../globals/Icons/SortColumnIcon.vue'
import CheckmarkIcon from '../../globals/Icons/CheckmarkIcon.vue'
import PinIcon from '../../globals/Icons/SpyderV2Icons/PinIcon.vue'
import PinOutlineIcon from '../../globals/Icons/SpyderV2Icons/PinOutlineIcon.vue'
import UnpinIcon from '../../globals/Icons/SpyderV2Icons/UnpinIcon.vue'
import InformationIcon from '../../globals/Icons/InformationIcon.vue'
import BarGraphIcon from "../../globals/Icons/LensIcons/graphs/BarGraphIcon.vue"
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import WeightIcon from '../../globals/Icons/LensIcons/WeightIcon.vue'
import SummationIcon from '../../globals/Icons/LensIcons/SummationIcon.vue'
import SameCreativeIcon from '../../globals/Icons/SameCreativeIcon.vue'

export default {
  name: 'LensMetricTable',
  components: {
    TablePagination,
    TableCell,
    StatusCell,
    TagCell,
    CreationDateRangeCell,
    HeaderActionCell,
    FooterCell,
    ColumnTooltips,

    AdDetailsAnimatedIcon,
    GroupDetailsIcon,
    InformationIcon,
    SortColumnIcon,
    CheckmarkIcon,
    BarGraphIcon,
    ChevronIcon,
    PinIcon,
    PinOutlineIcon,
    UnpinIcon,
    WeightIcon,
    SummationIcon,
    SameCreativeIcon
  },
  props: {
    reportType: {
      type: String,
      default: 'top-performing'
    },
    isAdLevel: {
      type: Boolean,
      default: () => false
    },
    groupedBy: {
      type: String,
      default: () => 'ad_name'
    },
    tablePagination: {
      type: Object,
      default: () => { },
    },
    data: {
      type: Object,
      default: () => { }
    },
    selectedColumns: {
      type: Array,
      default: () => []
    },
    selectedRows: {
      type: Array,
      default: () => []
    },
    tableColumns: {
      type: Array,
      default: () => []
    },
    tableConfig: {
      type: Object,
      default: () => { }
    },
    currentSort: {
      type: Object,
      default: () => { }
    },
    maxRowCount: {
      type: Number,
      default: () => 8
    },
    graphInfo: {
      type: Object,
      default: () => { }
    },
    loading: {
      type: Boolean,
      default: () => false
    },
    lensCurrency: {
      type: String,
      default: () => 'USD'
    },
    selectedCategoryGroup: {
      type: String,
      default: () => 'all'
    },
    categorizedRows: {
      type: Object,
      default: () => { }
    },
    searchQuery: {
      type: String,
      default: () => ''
    },
    hidePagination: {
      type: Boolean,
      default: false
    },
    stickyHeaderOffset: {
      type: Number,
      default: 0
    },
    floatHeaderEnabled: {
      type: Boolean,
      default: false
    },
    enableFloatHeaderToggle: {
      type: Boolean,
      default: false
    }
  },
  data() {

    const disabledAdLevelTooltips = ['groupTooltip']
    return {
      hoveredCell: {},
      disabledAdLevelTooltips,
      hoveredInsightsBtnId: null,
      hoveredDetailsBtnId: null,
      debouncedSearchQuery: '',
      searchDebounce: null,

      hiddenCategorizedRows: {},

      // Scroll virtualization
      overscan: 4,
      startIndex: 0,
      endIndex: 10,

      // Floating header
      showFloatingHeader: false,
      columnWidths: [],
      scrollAncestor: null,
      rafId: null,
      floatingHeaderPosition: { top: '0px', left: '0px', width: '0px' },

      // Scroll state tracking (avoids emitting on every scroll frame)
      lastScrollState: { canScrollLeft: false, canScrollRight: false },
    }
  },
  mounted () {
    this.checkScrollState()

    const scrollContainer = this.$refs.scrollContainer
    if (scrollContainer) {
      // Always add the basic scroll handler
      scrollContainer.addEventListener('scroll', this.handleScroll)

      // Also add scrollend event for modern browsers
      if ('onscrollend' in window) {
        scrollContainer.addEventListener('scrollend', this.handleScrollEnd)
      }
    }

    this.initCategoryCollapse()
    this.measureHeaderWidths()

    // Find and attach scroll ancestor listener
    this.scrollAncestor = this.findScrollAncestor()
    if (this.scrollAncestor) {
      this.scrollAncestor.addEventListener('scroll', this.updateFloatingHeaderThrottled, { passive: true })
    }

    // Add window resize listener
    window.addEventListener('resize', this.measureHeaderWidths)
    window.addEventListener('resize', this.updateFloatingHeaderThrottled)
  },
  beforeDestroy () {
    // Clear debounce timer
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce)
    }

    // Remove scroll event listeners
    const scrollContainer = this.$refs.scrollContainer
    if (scrollContainer) {
      scrollContainer.removeEventListener('scroll', this.handleScroll)

      // Also remove scrollend event if it was added
      if ('onscrollend' in window) {
        scrollContainer.removeEventListener('scrollend', this.handleScrollEnd)
      }
    }

    // Remove scroll ancestor listener
    if (this.scrollAncestor) {
      this.scrollAncestor.removeEventListener('scroll', this.updateFloatingHeaderThrottled)
    }

    // Cancel any pending animation frame
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
    }

    // Remove window resize listener
    window.removeEventListener('resize', this.measureHeaderWidths)
    window.removeEventListener('resize', this.updateFloatingHeaderThrottled)
  },
  watch: {
    searchQuery(newValue) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }

      this.searchDebounce = setTimeout(() => {
        this.debouncedSearchQuery = newValue
      }, 300)
    },
    categorizedRows () {
      if (this.tablePagination?.currentPage === 1) {
        this.initCategoryCollapse()
      }
      this.measureHeaderWidths()
    },
    tableKpis() {
      this.measureHeaderWidths()
    },
    pinnedColumns() {
      this.measureHeaderWidths()
    },
    data() {
      this.measureHeaderWidths()
    },
    loading(newVal) {
      if (newVal) {
        this.showFloatingHeader = false
      } else {
        this.updateFloatingHeaderThrottled()
      }
    },
    floatHeaderEnabled(enabled) {
      if (enabled) {
        // Trigger the float immediately if the user is already scrolled past
        // the real header
        this.measureHeaderWidths()
        this.updateFloatingHeaderThrottled()
      } else {
        this.showFloatingHeader = false
      }
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getMappedRowColor']),
    ...mapGetters('MiscModule', ['getIsPrivateMode']),
    ...mapGetters('MetricsModule', ['getMetricLookup', 'getAppliedPreset']),
    isCategorizedTable() {
      return this.categorizedRows && Object.keys(this.categorizedRows).length > 0
    },
    visibleKpis() {
      return this.isCategorizedTable ? this.tableKpis.slice(this.startIndex, this.endIndex) : []
    },
    showSelectAll() {
      return !this.isCategorizedTable
    },
    borderContainerStyle() {
      const noItems = this.tablePagination?.totalPages === 0
      const heightOffset = this.isCategorizedTable ? 0 : !noItems ? 52 : 0
      return {
        height: `calc(100% - ${heightOffset}px)`
      }
    },
    rows() {
      if (this.isAdLevel && this.data?.ads?.length > 0) {
        return this.data.ads
      } else if (this.data?.ad_groups?.length > 0) {
        return this.data.ad_groups
      }
      return []
    },
    netResults() {
      if (this.isAdLevel || this.data?.ad_groups?.length > 0) {
        return { ...this.data }
      }
      return {}
    },
    pinnedColumns() {
      return this.tableColumns.filter((col) => col.is_pinned);
    },
    // Sticky offsets for pinned columns, keyed by column key. Memoized so the
    // per-cell :style bindings don't recompute offsets (with layout reads)
    // for every pinned cell of every row on each re-render.
    pinOffsets() {
      const offsets = {}
      let acc = 320
      this.pinnedColumns.forEach((col) => {
        offsets[col.key] = acc
        const kpiIndex = this.tableKpis.findIndex((kpi) => kpi.key === col.key)
        acc += Math.ceil(this.columnWidths[kpiIndex + 1] || 0)
      })
      return offsets
    },
    tableKpis() {
      const showStatus = this.tableConfig?.showStatus
      const showTags = this.tableConfig?.showTags && (this.groupedBy === 'ad_name' || this.groupedBy === 'creative') 
      const showCreationRange = this.tableConfig?.showCreationRange && !this.isAdLevel

      // Get all columns that should be shown
      let allColumns = [...this.tableColumns]

      // Filter columns based on debounced search query
      if (this.debouncedSearchQuery && this.debouncedSearchQuery.trim() !== '') {
        const query = this.debouncedSearchQuery.toLowerCase().trim()
        allColumns = allColumns.filter(col => {
          const key = col.key.toLowerCase()
          const name = col.name ? col.name.toLowerCase() : ''
          return key.includes(query) || name.includes(query)
        })
      }

      if (this.pinnedColumns.length > 0) {
        return [
          ...(showStatus ? [{ key: 'status' }] : []),
          ...(showTags ? [{ key: 'tags' }] : []),
          ...(showCreationRange ? [{ key: 'creation_date_range', name: 'Creation Date Range' }] : []),
          ...[...allColumns].sort((a, b) => {
            const indexA = this.pinnedColumns.findIndex(pin => pin.key === a.key)
            const indexB = this.pinnedColumns.findIndex(pin => pin.key === b.key)
            if (indexA === -1 && indexB === -1) return 0
            if (indexA === -1) return 1
            if (indexB === -1) return -1
            return indexA - indexB
          })
        ]
      }

      return [
        ...(showStatus ? [{ key: 'status' }] : []),
        ...(showTags ? [{ key: 'tags' }] : []),
        ...(showCreationRange ? [{ key: 'creation_date_range', name: 'Creation Date Range' }] : []),
        ...allColumns
      ]
    },
    preventColAdd() {
      const currentGraph = this.graphInfo.selectedGraph
      const maxAllowed = this.graphInfo?.data?.[currentGraph].total || 0
      return this.selectedColumns?.length === maxAllowed
    },
    preventRowAdd() {
      return this.selectedRows?.length >= this.maxRowCount;
    },
    isAdDetailsEnabled() {
      return this.reportType !== 'comparative' || this.isAdLevel
    },
    canScrollLeft() {
      const container = this.$refs.scrollContainer
      // More strict check - ensure we have at least 1px of scrollable area
      return container ? Math.round(container.scrollLeft) > 0 : false
    },
    canScrollRight() {
      const container = this.$refs.scrollContainer
      if (!container) return false

      // More reliable check with explicit rounding
      const currentScroll = Math.round(container.scrollLeft)
      const maxScroll = Math.round(container.scrollWidth - container.clientWidth)

      // Only return false when we're at the very end
      return currentScroll < maxScroll
    },
    checkboxStates() {
      return this.rows.reduce(
        (acc, row) => {
          const isSelected = this.selectedRows.some(selectedRow => selectedRow.group_by === row.group_by);
          if (isSelected) {
            acc.checkedRows.push(row.group_by);
          } else {
            acc.unCheckedRows.push(row);
          }
          return acc;
        },
        { checkedRows: [], unCheckedRows: [] }
      );
    },
    disableToggleAll() {
      return this.preventRowAllAdd && this.checkboxStates.checkedRows.length === 0
    },
    selectedRowCount() {
      return this.selectedRows.length;
    },
    preventRowAllAdd() {
      const selectedMax =  (this.maxRowCount - this.selectedRowCount) === 0
      const noMoreSelections = this.checkboxStates.unCheckedRows?.length === 0
      return selectedMax || noMoreSelections
    },
    isCreativeGrouping () {
      return !this.isAdLevel 
        && (this.groupedBy === 'creative' || this.data?.group_by?.startsWith('https://lens.assets'))
    },
    floatingTableWidth() {
      return this.columnWidths.reduce((sum, width) => sum + (width || 0), 0)
    },
    floatingTableStyle() {
      return {
        tableLayout: 'fixed',
        borderCollapse: 'separate',
        borderSpacing: '0px',
        width: this.floatingTableWidth ? `${this.floatingTableWidth}px` : 'max-content'
      }
    }
  },
  methods: {
    ...mapActions('LensModule', ['updateRowColor', 'updateColorFormat', 'fetchAdGroups', 'updatePagination']),
    ...mapActions('MetricsModule', ['fetchPresets', 'updateLocalPresetField', 'setPresetById', 'updateIsCustomPreset']),
    getRowName (row) {
      if (this.getIsPrivateMode) return '•••••••••••••••••••'
      return this.isCreativeGrouping
        ? row?.ads?.[0]?.ad_name
        : (row.group_by || row.ad_name)
    },
    bindTagToAd(row, tag, index) {
      this.bindTagToCreative(tag, index, row)
    },
    removeTagFromAd(row, tag, index) {
      this.removeTagFromCreative(tag, index, row)
    },
    // Ad level binding
    async bindTagToCreative(tag, index, row) {
      const oldTags = row?.custom_tags || []
      try{
        // Update the row in the rows array
        this.$emit('updateTableResults', {
          ...(this.isAdLevel && { applyToHash: row.creative_hash }),
          index,
          key: 'custom_tags',
          value: [...(row?.custom_tags || []), tag]
        })
        const lensId = this.$route.params.lensId
        const hashesToBind = this.isAdLevel 
          ? [{creative_hash: row.creative_hash, tag_id: tag.tag_id, lens_id: lensId}] 
          : row.creative_hashes.map(hash => ({ creative_hash: hash, tag_id: tag.tag_id, lens_id: lensId}))
        const { data } = await LensAPI.Tags.tagCreative(hashesToBind)
      } catch (error) {
        this.$emit('updateTableResults', {
          ...(this.isAdLevel && { applyToHash: row.creative_hash }),
          index,
          key: 'custom_tags',
          value: oldTags
        })
        console.error('Error binding tag to creative', error)
      }
    },
    async removeTagFromCreative(tag, index, row) {
      const oldTags = row?.custom_tags || []
      try {
        this.$emit('updateTableResults', {
          ...(this.isAdLevel && { applyToHash: row.creative_hash }),
          index,
          key: 'custom_tags',
          value: oldTags.filter(t => t.tag_id !== tag.tag_id)
        })
        let tagsToRemove = []
        if (this.isAdLevel) {
          tagsToRemove = [{
            creative_hash: row.creative_hash,
            tag_id: tag.tag_id,
            lens_id: this.$route.params.lensId
          }]
        } else {
          const uniqueHashes = row.creative_hashes
          tagsToRemove = uniqueHashes.map(hash => ({ 
            creative_hash: hash, 
            tag_id: tag.tag_id, 
            lens_id: this.$route.params.lensId 
          }))
        }
        const { data } = await LensAPI.Tags.unTagCreative(tagsToRemove)
      } catch (error) {
        this.$emit('updateTableResults', {
          ...(this.isAdLevel && { applyToHash: row.creative_hash }),
          index,
          key: 'custom_tags',
          value: oldTags
        })
        console.error('Error removing tag from creative', error)
      }
    },
    initCategoryCollapse () {
      // Init first category collapse state
      if (this.isCategorizedTable) {
        const firstGroup = this.categorizedRows.ad_groups?.[0].group_by
        this.$set(this.hiddenCategorizedRows, firstGroup, true)
      }
    },
    handleCategoryCollapse(id) {
      if (this.hiddenCategorizedRows[id]) {
        this.$delete(this.hiddenCategorizedRows, id);
      } else {
        this.$set(this.hiddenCategorizedRows, id, true);
      }
    },
    showTooltip(refName, bool, event, text) {
      if (this.isAdLevel && this.disabledAdLevelTooltips.includes(refName)) return;

      const tooltipContainer = this.$refs.tooltipContainer;
      const includeTooltip = tooltipContainer.$refs[refName];
      if (includeTooltip && !bool) {
        includeTooltip.style.transition = 'opacity 0s ease 0ms';
        includeTooltip.style.opacity = '0'
      }
      else {
        if (text) tooltipContainer.updateTextCopy(refName, text);
        this.$nextTick(() => {
          const tooltipRect = includeTooltip.getBoundingClientRect()
          const referenceRect = event.target.getBoundingClientRect()
          // Calculate positions with offsets (in drawers, we need to offset the fixed position) TODO @sam: make it more versatile
          const offset = this.isAdLevel ? { top: -74, left: 56 } : { top: 8, left: 0 }
          const position = {
            top: referenceRect.top - tooltipRect.height - 12 + offset.top,
            left: referenceRect.left + (referenceRect.width / 2) - (tooltipRect.width / 2) + offset.left
          }
          includeTooltip.style.top = `${position.top}px`
          includeTooltip.style.left = `${position.left}px`
          // Add delay param later
          includeTooltip.style.transition = 'opacity 150ms ease 350ms';
          includeTooltip.style.opacity = '1'
        })
      }
    },
    handleCopySuccess() {
      this.$showAlert({
        type: 'success',
        message: `Copied ${this.groupedBy?.replace(/_/g, ' ')}`
      })
    },
    // uniqueAds(ads) {
    //   if (ads && ads.length > 0) {
    //     const adLimit = 20
    //     const groupBy = this.groupedBy === 'creative' ? 'ad_name' : this.groupedBy
    //     const uniqueDisplayValues = [...new Set(ads.map((ad) => ad?.[groupBy] || `${ad.ad_id} (Missing ${this.groupedBy})`))]
    //     if (uniqueDisplayValues.length > adLimit) {
    //       return [...uniqueDisplayValues.slice(0, adLimit), `+${uniqueDisplayValues.length - adLimit} more`]
    //     }
    //     return uniqueDisplayValues
    //   }
    //   return [];
    // },
    getGroupLabels(row) {
      const limit = 20
      const labels = row.group_labels.slice(0, limit)
      if (labels.length < row.group_labels.length) {
        labels.push(`+${row.group_labels.length - labels.length} more`)
      }
      return labels
    },
    // memoize the color format for a given value & kpi & color format
    showColoredCell: _.memoize(function (percentDiff, kpi, outlierFormat) {
      const summary = this.data?.summary_stats ?? this.categorizedRows.summary_stats;
      const metricDir = this.getMetricLookup?.[kpi]?.metric_direction
      return getRelativeMetricScoreColor(percentDiff, kpi, outlierFormat, summary, metricDir)
    }, (arg1, arg2, arg3) => `${arg1}-${arg2}-${arg3}`),
    getColoredCell(row, kpi) {
      if (this.isAdLevel || (!this.data?.summary_stats && !this.categorizedRows.summary_stats) || this.getKpiType(kpi) === 'percentile') return ''; // TODO: We might want deviation highlighting for ad level later

      const key = kpi.key;
      // TODO @Sam, maybe use memoized func directly, and add dependency callback as floored value to prevent new calcs
      const percentDiff = Math.floor(row.percent_deviation?.[key])
      return this.showColoredCell(percentDiff, key, this.tableConfig?.colorFormat);
    },
    getRowCheckboxColor(groupBy) {
      const currentGraph = this.graphInfo.selectedGraph
      if (this.isAdLevel || currentGraph === 'grid') return '#000'
      return this.getMappedRowColor[currentGraph][groupBy]
    },
    manageArrayAdd(arr, toAdd) {
      const exists = arr.includes(toAdd)
      return exists ? arr.filter((val) => val !== toAdd) : [...arr, toAdd]
    },
    // Column actions
    pinColumn(header) {
      // const newStickyColumns = this.manageArrayAdd(this.pinnedColumns, header)
      const newTableCols = [...this.tableColumns].map(col => {
        if (col.key === header.key) {
          return { ...col, is_pinned: !col.is_pinned };
        }
        return col;
      });
      this.$emit('reportChanged', { type: 'table_columns', value: newTableCols, preventFetch: true })
    },
    sortColumn(column, order) {
      const colKey = column.key

      const newSort = {
        by: colKey,
        order
      }
      const samekey = newSort.by === this.currentSort?.by;
      const sameOrder = newSort.order === this.currentSort?.order;
      if (samekey && sameOrder) return;
      // Prevent fetch on report change, as pagination will trigger a requery
      this.$emit('reportChanged', { type: 'sorted_column', value: newSort })
      this.$emit('paginationChanged', { type: null, value: { currentPage: 1 }, preventFetch: true })
    },
    selectColumn(column) {
      const colKey = column.key
      const updatedColumns = this.manageArrayAdd(this.selectedColumns, colKey)
      const existingCols = this.tableColumns.filter((col) => updatedColumns.includes(col.key)).map((col) => col.key)
      this.$emit('update:selectedColumns', existingCols)
    },
    selectRow(row) {
      const idKey = this.isAdLevel ? 'ad_id' : 'group_by';
      const rowId = row[idKey];

      const shouldRemoveRow = this.selectedRows.some(selectedRow => selectedRow[idKey] === rowId)
      const updatedRows = shouldRemoveRow
        ? this.selectedRows.filter(selectedRow => selectedRow[idKey] !== rowId)
        : [...this.selectedRows, row];

      this.updateRowColor({ keys: [row.group_by], isAdding: !shouldRemoveRow, graph: this.graphInfo.selectedGraph })
      this.$emit('update:selectedRows', updatedRows)
    },
    isRowSelected(id) {
      if (!this.selectedRows || this.selectedRows.length == 0) return;
      return this.selectedRows.findIndex((row) => this.isAdLevel ? row.ad_id === id : row.group_by === id) !== -1;
    },
    // TODO @Sam: clean up
    toggleAllRows(shouldClear) {
      // Override clear entire selection for graph
      if (shouldClear) {
        this.$emit('update:selectedRows', [])
        this.updateRowColor({ keys: [], isAdding: true, graph: this.graphInfo.selectedGraph })
        return;
      }
      // Left over # of rows we can select
      const rowsLeftToAdd = this.maxRowCount - this.selectedRowCount;
      const unCheckedRows = this.checkboxStates.unCheckedRows;
      const checkedRows = this.checkboxStates.checkedRows;

      const newSelection = this.preventRowAllAdd ? this.selectedRows.filter((row) => !checkedRows.includes(row.group_by)) : [...this.selectedRows, ...unCheckedRows.slice(0, rowsLeftToAdd)]
      const keyUpdate = unCheckedRows.length === 0 ? newSelection.filter((row) => !checkedRows.includes(row.group_by)) : newSelection
      this.$emit('update:selectedRows', keyUpdate)
      this.updateRowColor({ keys: keyUpdate, isAdding: !this.preventRowAllAdd, graph: this.graphInfo.selectedGraph })
    },
    calculatePinOffset(kpi) {
      return this.pinOffsets[kpi.key] ?? 320
    },
    getColumnColor(header) {
      const key = header.key;
      const cIndex = this.selectedColumns.findIndex((col) => col === key);

      return kpiColors[cIndex];
    },
    navToNetInfo() {
      window.open("https://foreplay.featurebase.app/en/help/articles/4240094-table-net-results", "_blank")
    },
    getMetadataNetResultLabel(col) {
      if (!col.is_metadata) return ''
      const numUnique = col.unique_values
      if (col.key === 'ad_copy') return `${numUnique} Ad ${numUnique === 1 ? 'Copy' : 'Copies'}`
      const name = col.name.replace(/\bname\b/gi, '').trim()
      return `${numUnique} ${name}${numUnique === 1 ? '' : 's'}`
    },
    scrollLeft() {
      const container = this.$refs.scrollContainer
      if (container) {
        // Store initial position to check if we actually scrolled
        const initialPosition = container.scrollLeft

        // Calculate visible width and scroll by that amount
        const scrollAmount = container.clientWidth - 320 // Subtract fixed column width
        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        })

        // Enhanced monitoring with more frequent updates
        this.monitorScrollProgress(initialPosition > 0)
      }
    },
    scrollRight() {
      const container = this.$refs.scrollContainer
      if (container) {
        // Store the maximum possible scroll position
        const maxScroll = container.scrollWidth - container.clientWidth

        // Calculate visible width and scroll by that amount
        const scrollAmount = container.clientWidth - 320 // Subtract fixed column width
        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        })

        // Enhanced monitoring with check for max scroll
        this.monitorScrollProgress(false, maxScroll)
      }
    },
    monitorScrollProgress(wasAtStart = false, maxScroll = 0) {
      // Check state immediately
      this.checkScrollState()

      // Start more intensive monitoring during the animation
      const container = this.$refs.scrollContainer

      // Then monitor progress at a higher frequency
      const scrollInterval = setInterval(() => {
        if (!container) {
          clearInterval(scrollInterval)
          return
        }

        this.checkScrollState()

        // Auto-detect if we're at an endpoint
        const currentScroll = Math.round(container.scrollLeft)
        const atStart = currentScroll === 0
        const atEnd = maxScroll > 0 && currentScroll >= maxScroll

        // If we've detected an endpoint, do a final check and stop monitoring
        if (atStart || atEnd) {
          this.checkScrollState()
          clearInterval(scrollInterval)
        }
      }, 30) // More frequent checks

      // Ensure we clean up the interval after a reasonable time
      setTimeout(() => {
        clearInterval(scrollInterval)
        this.checkScrollState() // Final check
      }, 600)
    },
    checkScrollState() {
      const container = this.$refs.scrollContainer
      if (container) {
        const scrollLeft = Math.round(container.scrollLeft)
        const scrollWidth = Math.round(container.scrollWidth)
        const clientWidth = Math.round(container.clientWidth)
        const maxScroll = Math.max(0, scrollWidth - clientWidth)

        // Only allow right scrolling if the content is actually wider than the container
        const hasHorizontalOverflow = scrollWidth > clientWidth

        const canScrollLeft = scrollLeft > 0
        const canScrollRight = hasHorizontalOverflow && scrollLeft < maxScroll

        // Only emit when the state actually changes. Emitting on every scroll
        // frame forces the (large) parent view to re-render each frame, which
        // is the primary cause of horizontal scroll jank.
        if (canScrollLeft !== this.lastScrollState.canScrollLeft ||
            canScrollRight !== this.lastScrollState.canScrollRight) {
          this.lastScrollState = { canScrollLeft, canScrollRight }
          this.$emit('scroll-state-change', {
            canScrollLeft,
            canScrollRight
          })
        }
      }
    },
    handleScroll() {
      this.checkScrollState()
      this.handleVirtualScroll();
      // Only mirror horizontal scroll position here
      this.syncFloatingHeaderScroll();
    },
    handleVirtualScroll() {
      // Column virtualization is only used by the categorized (comparative)
      // table. For the standard report table it is wasted work that reads every
      // header's layout box on each scroll frame, so skip it entirely.
      if (!this.isCategorizedTable) return

      const container = this.$refs.scrollContainer;
      const scrollLeft = container.scrollLeft;
      const viewportEnd = scrollLeft + container.clientWidth;
      const headers = this.$refs.headerKey;
      if (!headers) return

      let first = headers.findIndex(h =>
        h.offsetLeft + h.offsetWidth > scrollLeft
      );

      let after = headers.findIndex(h =>
        h.offsetLeft >= viewportEnd
      );
      if (after === -1) after = headers.length;
      this.startIndex = Math.max(first - this.overscan, 0);
      this.endIndex = Math.min(after + this.overscan, headers.length);
    },
    handleScrollEnd() {
      // Do a final check when scrolling ends
      this.checkScrollState()
    },
    resetTableScroll() {
      const container = this.$refs.scrollContainer
      if (container) {
        // Reset scroll to the leftmost position
        container.scrollLeft = 0

        // Update scroll state after reset
        this.checkScrollState()
      }
    },
    measureHeaderWidths() {
      // NOTE: columnWidths also drives pinned-column offsets (pinOffsets),
      // so this must run regardless of whether the floating header is enabled.
      this.$nextTick(() => {
        const container = this.$refs.scrollContainer
        if (!container) return

        const table = container.querySelector('table')
        if (!table) return

        const thead = table.querySelector('thead')
        if (!thead) return

        const headers = thead.querySelectorAll('th')
        const widths = Array.from(headers).map(th => th.getBoundingClientRect().width)
        this.columnWidths = widths
      })
    },
    syncFloatingHeaderScroll() {
      const container = this.$refs.scrollContainer
      const floatingHeader = this.$refs.floatingHeader
      if (container && floatingHeader) {
        floatingHeader.scrollLeft = container.scrollLeft
      }
    },
    toggleFloatHeader() {
      this.$emit('update:floatHeaderEnabled', !this.floatHeaderEnabled)
    },
    floatingHeaderCellStyle(index, header) {
      const width = this.columnWidths[index]
      const style = {}
      if (width != null) {
        style.width = `${width}px`
        style.minWidth = `${width}px`
        style.maxWidth = `${width}px`
      }
      if (header && header.is_pinned) {
        style.left = `${this.calculatePinOffset(header)}px`
      }
      return style
    },
    findScrollAncestor() {
      let el = this.$el?.parentElement
      while (el) {
        const computedStyle = window.getComputedStyle(el)
        const overflowY = computedStyle.overflowY
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          return el
        }
        el = el.parentElement
      }
      return window
    },
    updateFloatingHeaderThrottled() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
      }
      this.rafId = requestAnimationFrame(() => {
        this.updateFloatingHeader()
      })
    },
    updateFloatingHeader() {
      // Feature-gated: only float the header when the user has enabled it
      if (!this.floatHeaderEnabled) {
        if (this.showFloatingHeader) this.showFloatingHeader = false
        return
      }

      const container = this.$refs.scrollContainer
      if (!container) return

      // Hide floating header while loading
      if (this.loading) {
        if (this.showFloatingHeader) this.showFloatingHeader = false
        return
      }

      const table = container.querySelector('table')
      if (!table) return

      const thead = table.querySelector('thead')
      if (!thead) return

      const scrollAncestorTop = this.scrollAncestor === window || !this.scrollAncestor
        ? 0
        : this.scrollAncestor.getBoundingClientRect().top
      const threshold = scrollAncestorTop + this.stickyHeaderOffset

      const realHeaderRect = thead.getBoundingClientRect()
      const tableRect = table.getBoundingClientRect()
      const headerHeight = realHeaderRect.height

      const shouldShow = realHeaderRect.top < threshold && tableRect.bottom > threshold + headerHeight

      if (shouldShow) {
        const containerRect = container.getBoundingClientRect()
        const top = `${threshold}px`
        const left = `${containerRect.left}px`
        const width = `${container.clientWidth}px`
        // Only reassign (and re-patch styles) when the position actually
        // changes; while scrolling vertically these values are constant.
        const pos = this.floatingHeaderPosition
        if (pos.top !== top || pos.left !== left || pos.width !== width) {
          this.floatingHeaderPosition = { top, left, width }
        }
        const wasHidden = !this.showFloatingHeader
        this.showFloatingHeader = true
        // On the hidden -> visible transition, sync the horizontal scroll
        // position once the clone has been rendered.
        if (wasHidden) {
          this.$nextTick(() => this.syncFloatingHeaderScroll())
        }
      } else if (this.showFloatingHeader) {
        this.showFloatingHeader = false
      }
    },
    getPinStyle(header) {
      if (!header.is_pinned) return null;
      return { left: `${this.calculatePinOffset(header)}px` };
    },
    getCellStyle: _.memoize(function (kpi, row, cacheKey) {
      return {
        ...(kpi.is_pinned ? this.getPinStyle(kpi) : null),
        ...(row?.[kpi.key] !== 0 ? { backgroundColor: this.getColoredCell(row, kpi).scoreColor } : null),
        ...(kpi?.is_metadata ? { maxWidth: '222px' } : null),
      };
    }, (kpi, row, cacheKey) => cacheKey),
    getKpiType(kpi) {
      return this.getMetricLookup?.[kpi.key]?.type
    },
    formatLaunchDate(launchDate) {
      return new Date(launchDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    },
    formatNumber(value, type, currency) {
      return String(formatNumberLabel(value, type, currency))
    },
    isForeplayMetric(key) {
      return this.getMetricLookup?.[key]?.parent_group === 'foreplay_metrics'
    },
    isCustomConversionMetric(key) {
      return this.getMetricLookup?.[key]?.table_group === 'custom_conversions'
    },
    getRowAdCount (row) {
      if (this.isAdLevel) return 0
      return row.active_ad_count + row.paused_ad_count
    },
    toggleNetResults(type) {
      const newTableConfig = { ...this.tableConfig, [type]: !this.tableConfig?.[type] }
      this.$emit('reportChanged', { type: 'table_config', value: newTableConfig, preventFetch: true })
    },
    getCategorizedRowCountLabel(adCount) {
      const singular = adCount === 1
      switch (this.selectedCategoryGroup) {
        case 'winners': return `${adCount} ${singular ? 'Winning Creative' : 'Winning Creatives'}`
        case 'losers': return `${adCount} ${singular ? 'Losing Creative' : 'Losing Creatives'}`
        case 'scaling': return `${adCount} ${singular ? 'Scaling Creative' : 'Scaling Creatives'}`
        default: return `${adCount} ${singular ? 'New Creative' : 'New Creatives'}`
      }
    }
  }
}
</script>

<style scoped>
.scroll-container::-webkit-scrollbar {
  height: 16px;
}

.scroll-container::-webkit-scrollbar-track {
  background: #ffffff;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(236, 239, 243, 1);
  border-radius: 10px;
  border: 5px solid #ffffff;
  cursor: pointer;
  transition: border-color 150ms ease-in-out, border-width 150ms ease-in-out;
}

.scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(223, 225, 231, 1);
  border-width: 4px;
}

.scroll-container {
  transition: all 150ms;
  scroll-behavior: smooth;
}

/* Table Styles */
table {
  border-collapse: separate;
  border-spacing: 0px;
}

th,
td {
  background-color: white;
  border: none;
  margin: 0;
  padding: 0px;
}

/* Border Styles (rounded corners & bordered cells) */
table tr th:first-child .group-cell {
  border-top-left-radius: 6px;
  border-bottom: 1px solid #ECEFF3; 
}

table tr th:last-child .cell-header:last-child {
  border-right: 1px solid #ECEFF3;
  border-top: 1px solid #ECEFF3;
  border-bottom: 1px solid #ECEFF3;
}

table th:not(:first-child):not(:last-child) .cell-header {
  border-right: 1px solid #ECEFF3;
  border-top: 1px solid #ECEFF3;
  border-bottom: 1px solid #ECEFF3;
}

table tr td .cell {
  border-bottom: 1px solid #ECEFF3;
}

table tr td.sticky-column .cell,
table tfoot tr td .cell,
table tr td:last-child .cell {
  border-right: 1px solid #eceff3;
}


table tfoot tr td .group-cell {
  border-right: 1px solid #eceff3;
}

.table-cell>.group-cell {
  border-bottom: 1px solid #eceff3;
}


.table-cell {
  transition: colors 50ms ease-in-out;
  z-index: 2;
}


.cell {
  min-width: 132px;
  max-width: 100%;
}




/* Base cell highlight style */
.cell-highlight {
  position: absolute;
  top: -1px;
  left: 0px;
  height: calc(100% + 1px);
  width: calc(100% + 1px);
  z-index: 2;
  pointer-events: none;
}

.cell-red>>>.table-cell-value {
  color: #710E21
}

.cell-green>>>.table-cell-value {
  color: #184E44
}

.cell-green-lowAvg {
  background-color: #EFFEFA !important;
}

.cell-green-midAvg {
  background-color: #D8F3EE !important;
}

.cell-green-highAvg {
  background-color: #B9E9E0 !important;
}

.cell-red-lowAvg {
  background-color: #FFF0F3 !important;
}

.cell-red-midAvg {
  background-color: #FDD9E0 !important;
}

.cell-red-highAvg {
  background-color: #F9C8D2 !important;
}

.group-cell {
  border-color: #ECEFF3;
  width: 320px;
}


.sticky-column {
  position: sticky;
  left: 0;
  z-index: 3;
}

.column-actions>div {
  transition: opacity 150ms ease-in-out;
}

.truncate-group-tooltip {
  background: #24252D;
  bottom: calc(100% - 12px);
  opacity: 0;
  z-index: 999 !important;
  transition: all 150ms;
  pointer-events: none;

  left: 0;
  transform: translateY(0);
}

.truncate-group-trigger:hover+.truncate-group-tooltip {
  transform: translateY(-14px) !important;
  opacity: 1 !important;
}

/*
  Checkbox Styles
*/
.checkbox-input {
  box-shadow: 0px 0px 0px 1px #09194821, 0px 1px 2px 0px #09194821;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 150ms ease-in-out;
}

.checkbox-input:disabled {
  background-color: #ECEFF3;
  cursor: not-allowed;
}

.checkbox-input:checked {
  background-color: black;
}

table tr:hover>td:not(.disable-bg-hover) {
  background-color: #fcfcfc;
}

.select-row-info {
  transition: transform 175ms cubic-bezier(0.64, 0.57, 0.67, 1.53);
}

.select-row-wrapper:hover>.select-row-info {
  transform: translateY(-50%);
}
</style>
